import { selfAssessmentPath } from './studySelfAssessmentApi';
import type { StudySession } from './studyApi';
import { analysisPath, type StudyAnalysis, type StudyAnalysisQuestion } from './studyAnalysisApi';
import type { StudyAnalysisReviewItem } from './studyAnalysisFollowUpApi';

/**
 * A Study Session that works on one analysed question (study-capture-fixes-prd.md F-02, F-03):
 * the link to its analysis, its title and the next question.
 */

/** Session URL parameters that carry the analysed question. */
export const SESSION_ANALYSIS_PARAM = 'analysis';
export const SESSION_QUESTION_PARAM = 'question';

export interface AnalysisQuestionLink {
  analysisId: string;
  questionId: string;
}

/** "Q3" for question number "3"; a number that already starts with Q stays as it is. */
export function questionLabel(question: Pick<StudyAnalysisQuestion, 'number'>): string {
  return /^q/i.test(question.number) ? question.number : `Q${question.number}`;
}

/** The session URL for an analysed question; the ids let the page find its way back. */
export function questionSessionPath(sessionId: string, link: AnalysisQuestionLink): string {
  const params = new URLSearchParams({
    [SESSION_ANALYSIS_PARAM]: link.analysisId,
    [SESSION_QUESTION_PARAM]: link.questionId,
  });
  return `/learn/study/sessions/${encodeURIComponent(sessionId)}?${params}`;
}

/**
 * The analysed question of a session: from the session read when the server returns it,
 * otherwise from the session URL. Null for every other session.
 */
export function analysisQuestionLink(
  session: Pick<StudySession, 'analysis_question'> | null | undefined,
  params: URLSearchParams,
): AnalysisQuestionLink | null {
  const linked = session?.analysis_question;
  if (linked?.analysis_id && linked.question_id)
    return { analysisId: linked.analysis_id, questionId: linked.question_id };
  const analysisId = params.get(SESSION_ANALYSIS_PARAM);
  const questionId = params.get(SESSION_QUESTION_PARAM);
  return analysisId && questionId ? { analysisId, questionId } : null;
}

/** The list item id of a question, so "Back to questions" can scroll to it (F-02). */
export function analysisQuestionElementId(questionId: string): string {
  return `analysis-question-${questionId}`;
}

/** The analysis page, scrolled to one question ("Back to questions"). */
export function questionReturnPath(
  analysis: Pick<StudyAnalysis, 'id' | 'primary_document_id'>,
  questionId: string,
): string {
  const path = analysisPath(analysis.primary_document_id, analysis.id);
  return `${path}&${SESSION_QUESTION_PARAM}=${encodeURIComponent(questionId)}`;
}

/** The question with this id in the analysis shown now, or null when it was removed. */
export function findAnalysisQuestion(
  analysis: Pick<StudyAnalysis, 'result'> | null | undefined,
  questionId: string,
): StudyAnalysisQuestion | null {
  return analysis?.result?.questions.find((question) => question.id === questionId) ?? null;
}

/**
 * The next question that is not done, after the current one and then from the top. The current
 * question never counts: the learner just finished it, even when the list is not refreshed yet.
 */
export function nextOpenQuestion(
  analysis: Pick<StudyAnalysis, 'result'> | null | undefined,
  currentQuestionId: string,
): StudyAnalysisQuestion | null {
  const questions = analysis?.result?.questions ?? [];
  const position = questions.findIndex((question) => question.id === currentQuestionId);
  const ordered =
    position < 0 ? questions : [...questions.slice(position + 1), ...questions.slice(0, position)];
  return (
    ordered.find((question) => question.id !== currentQuestionId && question.status !== 'done') ??
    null
  );
}

/**
 * The session title for an analysed question (F-03): "Q3 · Quadratics", or "Q3 · Mathematics"
 * when the analysis has no topic. Null when the question is not known, so the caller keeps its
 * own title.
 */
export function questionSessionTitle(
  analysis: Pick<StudyAnalysis, 'result'> | null | undefined,
  questionId: string,
  subjectName: string,
): string | null {
  const question = findAnalysisQuestion(analysis, questionId);
  if (!question) return null;
  const topic = analysis?.result?.topic.label.trim();
  return `${questionLabel(question)} · ${topic || subjectName}`;
}

/** Analysis page parameter: opened from a Review item (study-capture-fixes-prd.md G-03). */
export const ANALYSIS_REVIEW_PARAM = 'review';

/**
 * The analysis page for a Review item, with "Practise again" and "Check my answers" on top. A
 * question item also scrolls to its question.
 */
export function analysisReviewPath(
  item: Pick<
    StudyAnalysisReviewItem,
    | 'analysis_id'
    | 'document_id'
    | 'question_id'
    | 'practice_set_id'
    | 'self_assessment'
    | 'subject'
  >,
): string {
  const path = analysisPath(item.document_id, item.analysis_id);
  if (item.self_assessment && item.practice_set_id)
    return `${selfAssessmentPath(item.analysis_id, item.practice_set_id, item.subject?.subject_key ?? 'mathematics')}&review=1${item.question_id ? `&question=${encodeURIComponent(item.question_id)}` : ''}`;
  if (item.practice_set_id) {
    return `${path}&practiceSet=${encodeURIComponent(item.practice_set_id)}&redo=1${item.question_id ? `&practiceQuestion=${encodeURIComponent(item.question_id)}` : ''}`;
  }
  const question = item.question_id
    ? `&${SESSION_QUESTION_PARAM}=${encodeURIComponent(item.question_id)}`
    : '';
  return `${path}&${ANALYSIS_REVIEW_PARAM}=1${question}`;
}
